// ===========================================
// Enquiry API Functions
// ===========================================
// The public enquiry form and the staff pipeline (enquiries plan §5.6, §5.7). Every type below
// is diffed against what the backend sends (services/enquiry/enquiryQuery.service.ts,
// enquiry.service.ts `getEnquiry`, the route envelopes): relations keep their PascalCase keys,
// `@db.Date` columns arrive as `YYYY-MM-DDT00:00:00.000Z` (read them with `formatUTCDate` or
// `localDateFromKey`, never `new Date()` + date-fns), and every day SENT is a `YYYY-MM-DD` key.

import type { SpamCheckStatus } from '../components/admin/SpamCheckChip';
import { apiClient } from '../services/api';

// ===========================================
// TYPES (Must match backend Prisma schema)
// ===========================================

/** The pipeline stage (§1a). `status` is a legacy column, dual-written from it until it is dropped. */
export type EnquiryStage =
  | 'NEW'
  | 'IN_CONVERSATION'
  | 'MNG_BOOKED'
  | 'MNG_FOLLOW_UP'
  | 'READY_TO_ONBOARD'
  | 'ONBOARDING'
  | 'WAITLIST'
  | 'ENROLLED'
  | 'NOT_PROCEEDING'
  | 'SPAM'
  | 'RESOLVED_TO_REVIEW';

/** The stages in pipeline order (the tabs follow it). */
export const ENQUIRY_STAGES: readonly EnquiryStage[] = [
  'NEW', 'IN_CONVERSATION', 'MNG_BOOKED', 'MNG_FOLLOW_UP', 'READY_TO_ONBOARD', 'ONBOARDING',
  'WAITLIST', 'ENROLLED', 'NOT_PROCEEDING', 'SPAM', 'RESOLVED_TO_REVIEW',
];

/** The legacy status, still sent with every row (read-only; never send it back). */
export type EnquiryStatus = 'NEW' | 'IN_PROGRESS' | 'REPLIED' | 'MEET_AND_GREET' | 'RESOLVED' | 'SPAM';

/** The CHANNEL it came in by (not the marketing source, which is `SourceOption`). */
export type EnquirySource = 'WEBSITE' | 'SCHOOL_PROFILE' | 'MANUAL' | 'EVENT';

export type EnquiryBallWith = 'SCHOOL' | 'FAMILY';
export type EnquiryTemperature = 'COLD' | 'WARM' | 'HOT';

export interface EnquiryReply {
  id: string;
  enquiryId: string;
  schoolId: string;
  userId: string;
  message: string;
  sentViaEmail: boolean;
  createdAt: string;
  updatedAt: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    /** The server no longer sends it (names only); nothing renders it. */
    email?: string;
  };
}

/** POST /:id/reply's result: the reply and whether its email went (`enquiryReply.service.ts`). */
export type EnquiryReplyResult = EnquiryReply & { emailSent: boolean | null };

/** The reply length the server accepts (`createReplySchema`). */
export const REPLY_MAX = 5_000;

export interface EnquiryChild {
  id: string;
  firstName: string | null;
  lastName: string | null;
  age: number | null;
  /** `@db.Date` (detail only). */
  dateOfBirth?: string | null;
  interest: string | null;
  /** List only: the school instrument the interest matched (its colour draws the Interest chip). */
  Instrument?: { id: string; name: string; color: string | null } | null;
  priorExperience?: string | null;
  sortOrder?: number;
  /** Detail only: set once the child is enrolled as a student (then it cannot be removed). */
  studentId?: string | null;
}

/** A flag the school raises on an active enquiry (plan §10). The SERVER decides which a row carries. */
export type EnquiryFlagKey =
  | 'MNG_NOTES_MISSING' | 'NEW_UNCONTACTED' | 'NO_ACTIVITY' | 'OVERDUE' | 'HOT_QUIET' | 'ONBOARDING_INCOMPLETE' | 'NO_OWNER' | 'WAITLIST_REVIEW';

/** GET /admin/enquiries/flags: a count per flag; null = switched off for the school (or not built yet). */
export interface EnquiryFlagCounts {
  counts: Record<EnquiryFlagKey, number | null>;
}

/** The fields the list and the detail share. */
interface EnquiryCore {
  id: string;
  schoolId: string;
  firstName: string;
  lastName: string;
  /** Null for a phone-only enquiry (Phase 2): an enquiry has an email OR a phone. */
  email: string | null;
  phone: string | null;
  subject: string | null;
  /** Legacy single child; the children are in `Children`. */
  childFirstName: string | null;
  instrument: string | null;
  source: EnquirySource;
  status: EnquiryStatus;
  stage: EnquiryStage;
  stageEnteredAt: string;
  /** `@db.Date`: the school-local day it was received (P48). */
  receivedOn: string;
  temperature: EnquiryTemperature | null;
  ballWith: EnquiryBallWith;
  /** `@db.Date` */
  chaseDate: string | null;
  nextAction: string | null;
  /** `@db.Date` */
  nextActionDueDate: string | null;
  assignedToId: string | null;
  /** The group the owner came from ('ADMIN_TIER' | 'ROLE'), when there is one. */
  ownerTarget: string | null;
  /** Archived once now >= archivedAt; may be in the FUTURE (Not Proceeding). */
  archivedAt: string | null;
  /** `@db.Date`. Non-null = "Needs review (migrated)" (P49). */
  migrationReviewDueAt: string | null;
  lastActivityAt: string;
  /** The enabled flags this row carries now, in the server's order (never computed in the browser). */
  flags: EnquiryFlagKey[];
  /** #412: Turnstile outcome for a PUBLIC enquiry; null = before #412 or staff-entered. */
  spamCheck?: SpamCheckStatus | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A named owner (BE `toOwnerView`): OWNER = the owner tier (shown with the school's owner title),
 * SUPPORT = a support account, STAFF = a live staff role (`roleName`), TEACHER = a teacher.
 */
export interface EnquiryOwner {
  id: string;
  firstName: string;
  lastName: string;
  labelKind: 'OWNER' | 'SUPPORT' | 'STAFF' | 'TEACHER' | null;
  roleName: string | null;
  /** STAFF only: the custom role's colour (#629). OWNER and TEACHER take the school's User Role Colors. */
  roleColor?: string | null;
}

export interface EnquiryListItem extends EnquiryCore {
  ownerRoleId: string | null;
  AssignedTo: EnquiryOwner | null;
  /** The role an unassigned enquiry waits with ("Unassigned · <role>"), with its colour (#629). */
  OwnerRole: { id: string; name: string; color?: string | null } | null;
  SourceOption: { id: string; label: string; systemKey: string | null } | null;
  Children: EnquiryChild[];
  MeetAndGreet: { id: string; status: string; scheduledDateTime: string | null } | null;
  _count: { Replies: number };
  /** Another enquiry of the school (any stage, archived too) has the same email or phone. */
  possibleDuplicate: boolean;
}

/** GET /admin/enquiries/:id. The submitter's IP address and browser are never sent (review 5.6 L7). */
export interface Enquiry extends EnquiryCore {
  /** Stored HTML-escaped (#298): decode with `unescapeHtml` before showing it. */
  message: string;
  trialType: string | null;
  ownerRoleId: string | null;
  enquirySourceId: string | null;
  closedReasonId: string | null;
  closedReasonDetail: string | null;
  waitlistProgram: string | null;
  waitlistPreferredDay: string | null;
  newsletterOptIn: boolean | null;
  availability: string | null;
  familyId: string | null;
  firstContactedAt: string | null;
  AssignedTo: EnquiryOwner | null;
  Replies: EnquiryReply[];
  _count: { Replies: number };
  MeetAndGreet: {
    id: string;
    status: string;
    createdAt: string;
    scheduledDateTime: string | null;
    /** The review day (ISO) the booking's waitlist clock holds. Read it only while `status` is WAITLISTED: leaving the waitlist does not clear it. */
    waitlistReviewAt?: string | null;
    /** The assigned teacher, names only: the owner an MNG_TEACHER stage defaults to (review 5.7 M7). */
    Teacher: { id: string; userId: string; User: { id: string; firstName: string; lastName: string } } | null;
  } | null;
  Children: EnquiryChild[];
  SourceOption: { id: string; label: string; systemKey: string | null; isActive: boolean } | null;
  ClosedReasonOption: { id: string; label: string; systemKey: string | null } | null;
  OwnerRole: { id: string; name: string } | null;
  Family: { id: string; name: string } | null;
  /** At Onboarding: the linked students' check-ins (Phase 6, BE `enquiryOnboardingBlock`). Null at any other stage. */
  onboarding?: EnquiryOnboardingBlock | null;
}

/** GET /admin/enquiries/:id `onboarding` (Phase 6). Dates are `@db.Date`; the state is the server's. */
export interface EnquiryOnboardingBlock {
  /** Children linked to a student. 0 = "Link the children to track onboarding". */
  linkedStudents: number;
  /** Linked students with no check-in (existing students). */
  withoutCheckIn: number;
  checkIns: Array<{
    studentId: string;
    name: string;
    firstClassDate: string | null;
    dueDate: string | null;
    state: 'NOT_SCHEDULED' | 'IN_PROGRESS' | 'DUE_TODAY' | 'OVERDUE' | 'DONE';
    done: number;
    total: number;
  }>;
}

/** One row of the history (append-only, newest first). */
export interface EnquiryActivity {
  id: string;
  schoolId: string;
  enquiryId: string;
  /** PHONE_CALL | EMAIL | SMS | MEET_AND_GREET | INTERNAL_NOTE | FOLLOW_UP | ENROLMENT | STAGE_CHANGE | SYSTEM */
  type: string;
  direction: 'OUTBOUND' | 'INBOUND' | 'INTERNAL';
  /** RAW text as typed (P44): React escapes it; never render it as HTML. */
  notes: string | null;
  nextAction: string | null;
  /** `@db.Date` */
  nextActionDueDate: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  fromStage: EnquiryStage | null;
  toStage: EnquiryStage | null;
  meetAndGreetId: string | null;
  details: Record<string, unknown>;
  performedById: string | null;
  performedByName: string;
  occurredAt: string;
  createdAt: string;
}

export interface EnquiryStageStats {
  /** Rows not archived, per stage. */
  byStage: Record<EnquiryStage, number>;
  /** The active pipeline (not archived, not Spam, not To review). */
  active: number;
  archived: number;
  /** Archived rows per stage (Enrolled and Spam archive on entry, so their tabs read these). */
  archivedByStage: Record<EnquiryStage, number>;
  /** The review screen's rows (RESOLVED_TO_REVIEW, not archived). */
  toReview: number;
  /** ACTIVE rows still marked "Needs review (migrated)". */
  needsReview: number;
  /** Active rows at a stage that should have a person, with none. */
  unassigned: number;
  total: number;
}

export interface EnquiryFilters {
  /** One stage; absent = the active pipeline. */
  stage?: EnquiryStage;
  /** 'true' = the archived rows (any stage). */
  archived?: 'true';
  /** 'true' = only rows marked "Needs review (migrated)". */
  needsReview?: 'true';
  /** A user id, 'me' or 'unassigned'. */
  owner?: string;
  /** One flag: only the active rows carrying it. */
  flag?: EnquiryFlagKey;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'nextActionDueDate' | 'receivedOn' | 'createdAt' | 'lastActivityAt' | 'stageEnteredAt' | 'firstName' | 'lastName';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedEnquiryResult {
  data: EnquiryListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ---- Config and settings ----

export type StageOwnerTarget =
  | { kind: 'ADMIN_TIER' }
  | { kind: 'ROLE'; roleId: string }
  | { kind: 'USER'; userId: string }
  | { kind: 'MNG_TEACHER' };

export type OwnedStage = 'NEW' | 'IN_CONVERSATION' | 'MNG_BOOKED' | 'MNG_FOLLOW_UP' | 'READY_TO_ONBOARD' | 'ONBOARDING' | 'WAITLIST';

export type NextActionKey =
  | 'NEW_ENQUIRY' | 'SCHEDULE_MNG' | 'HOLD_MNG' | 'SEND_INFO' | 'DECIDE_OUTCOME' | 'REBOOK_MNG'
  | 'MNG_CANCELLED' | 'READY_TO_ONBOARD' | 'ONBOARDING' | 'WAITLIST' | 'FAMILY_RETURNED' | 'FOLLOW_UP'
  | 'REVIEW_MIGRATED';

export type EnquiryTimingKey =
  | 'newDueBusinessDays' | 'scheduleMngBusinessDays' | 'followUpInfoBusinessDays' | 'replyChaseBusinessDays'
  | 'followUpChaseBusinessDays' | 'defaultDueBusinessDays' | 'newUncontactedHours' | 'noActivityDays'
  | 'hotNoActivityDays' | 'notProceedingArchiveDays' | 'onboardingWindowDays' | 'migrationReviewDays';


/** GET/PUT /admin/settings/enquiries (normalised: every key present). */
export interface EnquirySettings {
  stageEnabled: { WAITLIST: boolean; ONBOARDING: boolean };
  temperatureEnabled: boolean;
  /** Null = the built-in label. */
  stageLabels: Record<EnquiryStage, string | null>;
  stageOwners: Record<OwnedStage, StageOwnerTarget>;
  /** Null = the built-in wording. */
  nextActions: Record<NextActionKey, string | null>;
  timings: Record<EnquiryTimingKey, number>;
  flags: Record<EnquiryFlagKey, { enabled: boolean }>;
  requireNextActionOnStageChange: boolean;
  ballWithSchoolLabel: string | null;
  teacherNotesReminder: { enabled: boolean; graceMinutes: number; windowStartHour: number; windowEndHour: number };
}

/** A partial patch: any subset of keys, at any depth. */
export type EnquirySettingsPatch = { [K in keyof EnquirySettings]?: unknown };

export interface EnquiryStageView {
  stage: EnquiryStage;
  label: string;
  enabled: boolean;
  isExit: boolean;
  /** Carries an owner, a next action and a due date. */
  isOwned: boolean;
  owner: { target: StageOwnerTarget; userId: string | null; name: string } | null;
  /** What a manual move pre-fills; the due date is a `YYYY-MM-DD` key (the server's office days). */
  defaults: { nextAction: string; nextActionDueDate: string } | null;
}

export interface EnquiryClientConfig {
  stages: EnquiryStageView[];
  settings: EnquirySettings;
  meetAndGreetEnabled: boolean;
  timeZone: string;
  officeDays: number[];
  /** The SCHOOL's today, `YYYY-MM-DD`: compare due dates against it, never the browser's day. */
  schoolToday: string;
  ownerTitle: string;
  ownerTargetIssues: Array<{ stage: OwnedStage; target: StageOwnerTarget; reason: 'ROLE_NOT_FOUND' | 'ROLE_CANNOT_READ_ENQUIRIES' | 'USER_NOT_VALID' }>;
}

export type EnquiryListKind = 'SOURCE' | 'CLOSED_REASON';

export interface EnquiryListOption {
  id: string;
  schoolId: string;
  kind: EnquiryListKind;
  label: string;
  /** System rows: UNKNOWN_MIGRATED, EVENT (SOURCE), OTHER (CLOSED_REASON). */
  systemKey: string | null;
  isSystem: boolean;
  isSelectable: boolean;
  isActive: boolean;
  sortOrder: number;
  /** Owner-tier reads only; absent for everyone else. */
  createdById?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A saved reply (Phase 7): staff add, the owner tier renames, rewrites, hides and reorders. */
export interface EnquiryReplyTemplate {
  id: string;
  schoolId: string;
  name: string;
  /** Plain text with merge fields, as typed. */
  body: string;
  isActive: boolean;
  sortOrder: number;
  /** Owner-tier reads only; absent for everyone else. */
  createdById?: string | null;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

/** Mirrors the backend's `ENQUIRY_REPLY_MERGE_FIELDS` (`enquiryReplyMergeFields.ts`). */
export const REPLY_MERGE_FIELDS = ['parentName', 'childNames', 'schoolName', 'bookingLink'] as const;
/** Mirrors `TEMPLATE_NAME_MAX` / `TEMPLATE_BODY_MAX` (`enquiryReplyTemplate.service.ts`). */
export const TEMPLATE_NAME_MAX = 80;
export const TEMPLATE_BODY_MAX = 4_000;

/** POST /:id/reply-templates/:templateId/render. `text` is RAW; `omitted` = fields left out and why. */
export interface RenderedReplyTemplate {
  text: string;
  omitted: Array<{ field: 'bookingLink'; reason: 'ALREADY_BOOKED' | 'MEET_AND_GREET_OFF'; message: string }>;
}

export interface AssignableOwner {
  id: string;
  firstName: string;
  lastName: string;
  /** ADMIN = the owner tier; STAFF = through a staff role. */
  tier: 'ADMIN' | 'STAFF';
}

export type ReviewOutcome = 'ENROLLED' | 'NOT_PROCEEDING' | 'WAITLIST' | 'SPAM' | 'IN_CONVERSATION';

export interface MigrationReviewRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  /** `@db.Date` */
  receivedOn: string;
  /** Stored HTML-escaped (#298). */
  message: string;
  familyId: string | null;
  suggestedOutcome: 'ENROLLED' | null;
  suggestedFamilyId: string | null;
  /** The suggested family's name, so "Enrolled" says which family it links. */
  suggestedFamilyName: string | null;
  duplicates: Array<{ id: string; stage: EnquiryStage; receivedOn: string }>;
}

// ---- Request bodies ----

/** POST /:id/transition. `expectedFromStage` is REQUIRED: the stage the page showed (a 409 if it moved). */
export interface TransitionEnquiryData {
  toStage: EnquiryStage;
  expectedFromStage: EnquiryStage;
  /** A person; null = the stage's default owner. */
  ownerUserId?: string | null;
  nextAction?: string;
  /** `YYYY-MM-DD`, not before the school's today. */
  nextActionDueDate?: string;
  closedReasonId?: string;
  closedReasonDetail?: string;
  waitlistProgram?: string;
  waitlistPreferredDay?: string;
  /** Required for MNG Follow Up (the Meet & Greet notes). */
  note?: string;
}

/**
 * PUT /:id/next-action. `ownerUserId: null` = back to the group the owner came from. The next
 * action and due day go together, or neither: "Take it" sends only the owner and keeps them.
 */
export interface SetNextActionData {
  ownerUserId: string | null;
  nextAction?: string;
  /** `YYYY-MM-DD` */
  nextActionDueDate?: string;
}

/** One child on the add form (no id) or the details PATCH (id = an existing child). */
export interface EnquiryChildInput {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  age?: number | null;
  /** `YYYY-MM-DD` */
  dateOfBirth?: string | null;
  interest?: string | null;
  priorExperience?: string | null;
}

/** PATCH /:id: details only. '' or null email = none; the enquiry must keep an email or a phone. */
export interface UpdateEnquiryData {
  firstName?: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  /** The full list, in order: a child left out is removed (never one enrolled as a student). */
  children?: EnquiryChildInput[];
  temperature?: EnquiryTemperature | null;
  availability?: string | null;
  enquirySourceId?: string;
  newsletterOptIn?: boolean | null;
}

/** POST /matches (warn only). */
export interface EnquiryMatches {
  enquiries: Array<{
    id: string;
    firstName: string;
    lastName: string;
    stage: EnquiryStage;
    /** `YYYY-MM-DD` */
    receivedOn: string;
    archived: boolean;
    matchedOn: Array<'EMAIL' | 'PHONE'>;
  }>;
  /** The active family the email belongs to. */
  families: Array<{ familyId: string; name: string }>;
}

export type LoggableActivityType = 'PHONE_CALL' | 'SMS' | 'EMAIL' | 'INTERNAL_NOTE';

/** POST /:id/activities: "+ Call", "+ Note". */
export interface LogActivityData {
  type: LoggableActivityType;
  /** A call, text or email; a note is internal. */
  direction?: 'OUTBOUND' | 'INBOUND';
  notes?: string | null;
  /** An instant (`toISOString()`), never in the future. */
  occurredAt?: string;
  nextAction?: string;
  /** `YYYY-MM-DD` */
  nextActionDueDate?: string;
  ownerUserId?: string | null;
}

/** POST /:id/family-replied. */
export interface FamilyRepliedData {
  channel?: 'EMAIL' | 'PHONE_CALL' | 'SMS';
  note?: string | null;
}

/** POST /:id/link-family: a family, a student, or both. */
export interface LinkFamilyData {
  familyId?: string;
  studentId?: string;
  childId?: string;
}

export interface PublicEnquirySubmitData {
  schoolSlug: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  childFirstName?: string;
  instrument?: string;
  trialType?: string;
  subject?: string;
  message: string;
  website?: string; // honeypot field
  /** #412: the Turnstile token, or '' when the widget produced none (the server asks Cloudflare either way). */
  turnstileToken?: string;
}

/** The manual form's limits, as `createManualEnquirySchema` (BE `validators/enquiry.validators.ts`). */
export const MANUAL_ENQUIRY_LIMITS = { name: 50, phone: 20, child: 100, subject: 200, message: 2_000 } as const;

/**
 * POST /admin/enquiries/manual. The channel is always MANUAL; the marketing source is required.
 * Required (§7): the contact's first name, an email OR a phone, the source, the message.
 */
export interface ManualEnquiryData {
  firstName: string;
  lastName?: string;
  /** '' = none. */
  email?: string;
  phone?: string;
  /** Several children, or none. */
  children?: Omit<EnquiryChildInput, 'id'>[];
  trialType?: string;
  subject?: string;
  /** Up to 2,000 characters. */
  message: string;
  temperature?: EnquiryTemperature | null;
  availability?: string | null;
  /** Unticked by default. */
  newsletterOptIn?: boolean;
  /** A selectable SOURCE option (`forNewEnquiry` list). */
  enquirySourceId: string;
  /** "Date received", `YYYY-MM-DD`; absent = the school's today; a later day is a 400. */
  receivedOn?: string;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// PUBLIC API (No Auth)
// ===========================================

export const enquiryPublicApi = {
  /**
   * Submit an enquiry from the public enquiry form
   */
  submit: (data: PublicEnquirySubmitData) =>
    apiClient
      .post<ApiResponse<{ id: string; message: string }>>(
        '/public/enquiries',
        data
      )
      .then((res) => res.data),
};

// ===========================================
// ADMIN API (Auth Required)
// ===========================================

const base = '/admin/enquiries';

export const enquiryApi = {
  /** The list: the active pipeline, one stage, or the archived rows. */
  list: (filters?: EnquiryFilters) =>
    apiClient.get<ApiResponse<PaginatedEnquiryResult>>(base, { params: filters }).then((res) => res.data),

  get: (id: string) => apiClient.get<ApiResponse<Enquiry>>(`${base}/${id}`).then((res) => res.data),

  /** A count per flag (null = off). Needs `enquiries.read`. */
  getFlags: () => apiClient.get<ApiResponse<EnquiryFlagCounts>>(`${base}/flags`).then((res) => res.data),

  getStats: () => apiClient.get<ApiResponse<EnquiryStageStats>>(`${base}/stats`).then((res) => res.data),

  /** Stages, labels, owners, defaults and the school's today. */
  getConfig: () => apiClient.get<ApiResponse<EnquiryClientConfig>>(`${base}/config`).then((res) => res.data),

  /** The history, newest first. */
  activities: (id: string, params?: { page?: number; limit?: number }) =>
    apiClient
      .get<ApiResponse<{ data: EnquiryActivity[]; total: number; page: number; limit: number }>>(`${base}/${id}/activities`, { params })
      .then((res) => res.data),

  /** Move the stage (the server applies the seven rules, §5.4). */
  transition: (id: string, data: TransitionEnquiryData) =>
    apiClient.post<ApiResponse<Enquiry>>(`${base}/${id}/transition`, data).then((res) => res.data),

  setNextAction: (id: string, data: SetNextActionData) =>
    apiClient.put<ApiResponse<Enquiry>>(`${base}/${id}/next-action`, data).then((res) => res.data),

  archive: (id: string) => apiClient.post<ApiResponse<Enquiry>>(`${base}/${id}/archive`).then((res) => res.data),

  unarchive: (id: string) => apiClient.post<ApiResponse<Enquiry>>(`${base}/${id}/unarchive`).then((res) => res.data),

  /** The owner tier only. */
  delete: (id: string) =>
    apiClient.delete<{ status: string; message: string }>(`${base}/${id}`).then((res) => res.message),

  /**
   * `emailSent`: true = emailed; false = the email FAILED (saved to the history, the family has NOT
   * received it); null = kept as a note.
   */
  reply: (id: string, data: { message: string; sentViaEmail?: boolean }) =>
    apiClient.post<ApiResponse<EnquiryReplyResult>>(`${base}/${id}/reply`, data).then((res) => res.data),

  createManual: (data: ManualEnquiryData) =>
    apiClient.post<ApiResponse<Enquiry>>(`${base}/manual`, data).then((res) => res.data),

  /** Details only (contacts, children, temperature, availability, source, opt-in). */
  update: (id: string, data: UpdateEnquiryData) =>
    apiClient.patch<ApiResponse<Enquiry>>(`${base}/${id}`, data).then((res) => res.data),

  // ---- Contact with the family, duplicates, family links (Phase 2) ----

  /** A POST so an email or a phone never lands in an access log. */
  matches: (data: { email?: string; phone?: string; excludeEnquiryId?: string }) =>
    apiClient.post<ApiResponse<EnquiryMatches>>(`${base}/matches`, data).then((res) => res.data),

  logActivity: (id: string, data: LogActivityData) =>
    apiClient.post<ApiResponse<Enquiry>>(`${base}/${id}/activities`, data).then((res) => res.data),

  familyReplied: (id: string, data: FamilyRepliedData) =>
    apiClient.post<ApiResponse<Enquiry>>(`${base}/${id}/family-replied`, data).then((res) => res.data),

  /** `alreadyLinked` sits BESIDE `data`. */
  linkFamily: (id: string, data: LinkFamilyData) =>
    apiClient
      .post<ApiResponse<Enquiry> & { alreadyLinked: boolean }>(`${base}/${id}/link-family`, data)
      .then((res) => ({ enquiry: res.data, alreadyLinked: res.alreadyLinked })),

  // ---- Owners and lists ----

  assignableOwners: () =>
    apiClient.get<ApiResponse<AssignableOwner[]>>(`${base}/assignable-owners`).then((res) => res.data),

  listOptions: (kind: EnquiryListKind, opts: { includeHidden?: boolean; forNewEnquiry?: boolean } = {}) =>
    apiClient
      .get<ApiResponse<EnquiryListOption[]>>(`${base}/options`, {
        params: {
          kind,
          ...(opts.includeHidden ? { includeHidden: 'true' } : {}),
          ...(opts.forNewEnquiry ? { forNewEnquiry: 'true' } : {}),
        },
      })
      .then((res) => res.data),

  /** Any staff member may add. `existed` sits BESIDE `data`: the name was already there. */
  addOption: (kind: EnquiryListKind, label: string) =>
    apiClient
      .post<ApiResponse<EnquiryListOption> & { existed: boolean }>(`${base}/options`, { kind, label })
      .then((res) => ({ option: res.data, existed: res.existed })),

  /** Rename, hide or restore: the owner tier only. */
  updateOption: (optionId: string, patch: { label?: string; isActive?: boolean }) =>
    apiClient.patch<ApiResponse<EnquiryListOption>>(`${base}/options/${optionId}`, patch).then((res) => res.data),

  /** Every option of the kind, hidden ones included, exactly once: the owner tier only. */
  reorderOptions: (kind: EnquiryListKind, orderedIds: string[]) =>
    apiClient.put<ApiResponse<EnquiryListOption[]>>(`${base}/options/order`, { kind, orderedIds }).then((res) => res.data),

  // ---- Saved reply templates (Phase 7) ----

  /** The active templates; with `includeHidden`, the hidden ones too (honoured for the owner tier only). */
  listTemplates: (opts: { includeHidden?: boolean } = {}) =>
    apiClient
      .get<ApiResponse<EnquiryReplyTemplate[]>>(`${base}/reply-templates`, { params: opts.includeHidden ? { includeHidden: 'true' } : {} })
      .then((res) => res.data),

  /** Anyone with enquiries.write. 422 `TEMPLATE_UNKNOWN_FIELD`, 409 `TEMPLATE_NAME_TAKEN`. */
  addTemplate: (data: { name: string; body: string }) =>
    apiClient.post<ApiResponse<EnquiryReplyTemplate>>(`${base}/reply-templates`, data).then((res) => res.data),

  /** Rename, rewrite, hide or restore: the owner tier only. */
  updateTemplate: (templateId: string, patch: { name?: string; body?: string; isActive?: boolean }) =>
    apiClient.patch<ApiResponse<EnquiryReplyTemplate>>(`${base}/reply-templates/${templateId}`, patch).then((res) => res.data),

  /** Every template, hidden ones included, exactly once: the owner tier only. */
  reorderTemplates: (orderedIds: string[]) =>
    apiClient.put<ApiResponse<EnquiryReplyTemplate[]>>(`${base}/reply-templates/order`, { orderedIds }).then((res) => res.data),

  /** The template filled for this enquiry (enquiries.write; writes nothing). */
  renderTemplate: (enquiryId: string, templateId: string) =>
    apiClient
      .post<ApiResponse<RenderedReplyTemplate>>(`${base}/${enquiryId}/reply-templates/${templateId}/render`)
      .then((res) => res.data),

  // ---- The review screen (§6.3) ----

  migrationReview: () =>
    apiClient.get<ApiResponse<MigrationReviewRow[]>>(`${base}/migration-review`).then((res) => res.data),

  reviewOutcome: (id: string, data: { outcome: ReviewOutcome; familyId?: string }) =>
    apiClient.post<ApiResponse<Enquiry>>(`${base}/${id}/review-outcome`, data).then((res) => res.data),

  // ---- Link an existing Meet & Greet (§8.2; needs meet_and_greet.write AND enquiries.write) ----

  linkMeetAndGreet: (meetAndGreetId: string, enquiryId: string) =>
    apiClient
      .post<ApiResponse<{ meetAndGreetId: string; enquiryId: string; replacedMeetAndGreetId: string | null; alreadyLinked: boolean }>>(
        `/admin/meet-and-greet/${meetAndGreetId}/link-enquiry`,
        { enquiryId },
      )
      .then((res) => res.data),

  /**
   * The signed Meet & Greet booking link for this enquiry (`enquiries.write`). 409
   * `ENQUIRY_ALREADY_LINKED` while the enquiry holds a booking that cannot be replaced.
   */
  bookingLink: (id: string) =>
    apiClient.get<ApiResponse<{ url: string; token: string }>>(`${base}/${id}/booking-link`).then((res) => res.data),

  // ---- Settings → Enquiries (system_settings read / write) ----

  getSettings: () => apiClient.get<ApiResponse<EnquirySettings>>('/admin/settings/enquiries').then((res) => res.data),

  updateSettings: (patch: EnquirySettingsPatch) =>
    apiClient.put<ApiResponse<EnquirySettings>>('/admin/settings/enquiries', patch).then((res) => res.data),

  resetSettings: () =>
    apiClient.post<ApiResponse<EnquirySettings>>('/admin/settings/enquiries/reset').then((res) => res.data),
};
